import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import { createUseStyles } from 'react-jss';
import { Donor } from '../../slices/donor';
import {
  createGradientString,
  getFallbackGradient,
} from '../../utils/colorExtraction.utils';
// Force recompile
const { Title } = Typography;

const useStyles = createUseStyles({
  headerContainer: {
    width: '100%',
    maxWidth: '1119px',
    height: '230px',
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
    marginBottom: '20px',
  },
  gradientBackground: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    padding: '28px',
    position: 'relative',
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '24px',
    width: '100%',
  },
  logoContainer: {
    width: '140px',
    height: '140px',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: '10px',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
  },
  ficheLabel: {
    fontFamily: 'PP Mori',
    fontWeight: '600 !important',
    fontSize: '20px !important',
    lineHeight: '24px !important',
    letterSpacing: '-0.02em !important',
    margin: '0 !important',
    color: 'rgba(255, 255, 255, 0.9) !important',
    textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  donorName: {
    fontFamily: 'PP Mori',
    fontWeight: '700 !important',
    fontSize: '56px !important',
    lineHeight: '60px !important',
    margin: '0 !important',
    color: '#ffffff !important',
    textShadow: '0 2px 12px rgba(0, 0, 0, 0.4)',
    wordBreak: 'break-word',
  },
  noLogoHeader: {
    height: '180px',
    background:
      'linear-gradient(135deg, #5050F9 0%, #3838C7 50%, #2D2DA5 100%)',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  noLogoFicheLabel: {
    fontFamily: 'PP Mori',
    fontWeight: '600 !important',
    fontSize: '28px !important',
    lineHeight: '34px !important',
    letterSpacing: '-0.02em !important',
    marginBottom: '12px !important',
    color: 'rgba(255, 255, 255, 0.9) !important',
    textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  noLogoDonorName: {
    fontFamily: 'PP Mori',
    fontWeight: '600',
    fontSize: '50px !important',
    lineHeight: '52.5px !important',
    margin: '0 !important',
    color: '#ffffff !important',
    textShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
  },
});

interface Props {
  displayedDonor: Donor;
}

const DonorHeader: React.FC<Props> = ({ displayedDonor }) => {
  const classes = useStyles();
  const [gradientBackground, setGradientBackground] = useState<string>(
    getFallbackGradient(),
  );
  const [isLoadingColors, setIsLoadingColors] = useState<boolean>(false);

  useEffect(() => {
    if (displayedDonor.logoUrl) {
      setIsLoadingColors(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:9000';
      const encodedLogoUrl = encodeURIComponent(displayedDonor.logoUrl);

      fetch(`${apiUrl}/image-proxy/gradient?url=${encodedLogoUrl}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data: { colors: string[] }) => {
          const gradient = createGradientString(data.colors);
          setGradientBackground(gradient);
        })
        .catch((error) => {
          console.error(
            '🎨 [DonorHeader] Failed to fetch gradient colors:',
            error,
          );
          setGradientBackground(getFallbackGradient());
        })
        .finally(() => {
          setIsLoadingColors(false);
        });
    }
  }, [displayedDonor.logoUrl]);

  if (!displayedDonor.logoUrl) {
    return (
      <div className={`${classes.headerContainer} ${classes.noLogoHeader}`}>
        <Title className={classes.noLogoFicheLabel}>Fiche Mécène</Title>
        <Title className={classes.noLogoDonorName}>{displayedDonor.name}</Title>
      </div>
    );
  }

  return (
    <div className={classes.headerContainer}>
      <div
        className={classes.gradientBackground}
        style={{
          background: gradientBackground,
          transition: isLoadingColors ? 'none' : 'background 0.5s ease',
        }}
      >
        <div className={classes.contentWrapper}>
          <div className={classes.logoContainer}>
            <img
              src={displayedDonor.logoUrl}
              alt={`${displayedDonor.name} logo`}
              className={classes.logoImage}
            />
          </div>
          <div className={classes.textContainer}>
            <Title className={classes.ficheLabel}>Fiche Mécène</Title>
            <Title className={classes.donorName}>{displayedDonor.name}</Title>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonorHeader;
